import { Database, RotateCcw, Sparkles, Zap } from 'lucide-react'
import type { DemoSourceSummary } from '../../models/demoToneAnalysis'
import type { ToneSignatureUiCategory, ToneSignatureUiElement, ToneSignatureUiModel } from '../../models/toneSignature'

type ToneSignaturePageProps = {
  toneSignature: ToneSignatureUiModel
  sourceSummary: DemoSourceSummary | null
  onAnalyzeAnother: () => void
  onGeneratePreset: () => void
}

function formatConfidence(value: number) {
  return `${Math.round(value * 100)}%`
}

function formatCategory(value: string) {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function EvidenceCounts({ element }: { element: ToneSignatureUiElement }) {
  return <small>
    {element.evidenceSummary.supportCount} support / {element.evidenceSummary.independentSourceCount} sources
  </small>
}

function ToneSignatureCategory({ category }: { category: ToneSignatureUiCategory }) {
  return <section className="ts-tone-card">
    <div className="ts-tone-card-head">
      <Zap size={18}/>
      <h4>{formatCategory(category.category)}</h4>
    </div>
    <div className="ts-tone-list">
      {category.elements.map((element) => <article key={`${category.category}-${element.role}-${element.rank}-${element.value}`}>
        <div>
          <span>{formatCategory(element.role)}</span>
          <b>{element.value}</b>
          <EvidenceCounts element={element}/>
        </div>
        <strong>{formatConfidence(element.confidence)}</strong>
      </article>)}
    </div>
  </section>
}

export function ToneSignaturePage({
  toneSignature,
  sourceSummary,
  onAnalyzeAnother,
  onGeneratePreset,
}: ToneSignaturePageProps) {
  return <div className="ts-tone-signature">
    <section className="ts-tone-hero">
      <span className="ts-demo-badge">TONE SIGNATURE</span>
      <div>
        <h3>{toneSignature.title}</h3>
        <p>{toneSignature.artist}</p>
        {sourceSummary?.name && <small>{sourceSummary.type}: {sourceSummary.name}</small>}
        <small>Knowledge synthesis {toneSignature.synthesisVersion} - {toneSignature.confidenceBand}</small>
      </div>
      <div className="ts-tone-confidence">
        <span>Confidence</span>
        <b>{formatConfidence(toneSignature.confidence)}</b>
        <i><em style={{ width: formatConfidence(toneSignature.confidence) }}/></i>
      </div>
    </section>

    <section className="ts-tone-summary-card">
      <div className="ts-tone-card-head">
        <Sparkles size={18}/>
        <h4>Knowledge Evidence</h4>
      </div>
      <div className="ts-tone-spec-grid">
        <div><span>Support</span><b>{toneSignature.evidenceSummary.supportCount}</b></div>
        <div><span>Context</span><b>{toneSignature.evidenceSummary.contextCount}</b></div>
        <div><span>Contradictions</span><b>{toneSignature.evidenceSummary.contradictionCount}</b></div>
        <div><span>Sources</span><b>{toneSignature.evidenceSummary.independentSourceCount}</b></div>
      </div>
    </section>

    {toneSignature.categories.length > 0 ? <div className="ts-tone-grid">
      {toneSignature.categories.map((category) => <ToneSignatureCategory key={category.category} category={category}/>)}
    </div> : <section className="ts-tone-card">
      <div className="ts-tone-card-head">
        <Database size={18}/>
        <h4>No Visible Elements</h4>
      </div>
    </section>}

    <div className="ts-analysis-actions">
      <button className="button ghost" type="button" onClick={onAnalyzeAnother}>
        <RotateCcw size={17}/> Analyze another song
      </button>
      <button className="button" type="button" onClick={onGeneratePreset}>
        Generate Helix Preset &mdash; Coming Soon
      </button>
    </div>
  </div>
}
